import { cn } from '#app/utils/misc.tsx'

interface UserAvatarProps {
	name: string | null | undefined
	username: string
	/**
	 * Tailwind classes for sizing / positioning (e.g. "h-11 w-11 text-sm" or
	 * "h-full w-full text-base"). The parent decides the dimensions — this
	 * component only provides the shape, background, and initials.
	 */
	className?: string
}

function getInitials(
	name: string | null | undefined,
	username: string,
): string {
	if (name) {
		const parts = name
			.trim()
			.split(/\s+/)
			.filter(Boolean)
		if (parts.length >= 2) {
			const first = parts[0]?.[0] ?? ''
			const last = parts[parts.length - 1]?.[0] ?? ''
			return (first + last).toUpperCase()
		}
		if (parts[0]) {
			return parts[0].slice(0, 2).toUpperCase()
		}
	}
	return username.slice(0, 2).toUpperCase()
}

/**
 * UserAvatar — initials-based avatar on the PLQ primary background.
 *
 * Our members never upload photos, so we render their initials (or the first
 * two letters of the username as fallback). The parent controls sizing via
 * className.
 *
 * Example:
 *   <UserAvatar name={user.name} username={user.username} className="h-12 w-12 text-base" />
 */
export function UserAvatar({ name, username, className }: UserAvatarProps) {
	const initials = getInitials(name, username)
	return (
		<span
			aria-hidden="true"
			className={cn(
				'bg-primary text-primary-foreground inline-flex items-center justify-center rounded-full font-semibold tracking-wide select-none',
				className,
			)}
		>
			{initials}
		</span>
	)
}
